import * as React from 'react';
import { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import { BaseHero, Text } from '@blueprint/ui';
import ErrorImage from './ErrorImage';
import { logError } from '@zoopla/logger';

type Props = {
    children: React.ReactNode;
    renderOnError?: React.ReactNode | null;
};

type State = {
    hasError: boolean;
};

class ErrorBoundary extends React.Component<Props, State> {
    override state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        logError(error, errorInfo);
        if (typeof window !== 'undefined') {
            window.newrelic?.noticeError(error, {
                componentStack: errorInfo.componentStack
            });
        }
    }

    override render() {
        const hasDefinedOrNullRenderOnError =
            this.props.renderOnError || this.props.renderOnError === null;

        if (this.state.hasError) {
            return hasDefinedOrNullRenderOnError ? (
                this.props.renderOnError
            ) : (
                <BaseHero
                    title="Something went wrong"
                    image={ErrorImage}
                    content={
                        <Text>
                            Our team has been made aware and are working to find
                            a solution to this problem. Please refresh your page
                            or, if the issue continues, please try again later.
                        </Text>
                    }
                />
            );
        }
        return this.props.children;
    }
}
export default ErrorBoundary;

// https://github.com/emotion-js/emotion/issues/2169
// Type hack can be removed with ZDS
export function withErrorBoundary<P>(
    WrappedComponent: React.ComponentType<React.PropsWithChildren<P>>,
    renderOnError: React.ReactNode
): React.ComponentType<React.PropsWithChildren<P>> {
    return (props: P) => (
        <ErrorBoundary renderOnError={renderOnError}>
            <WrappedComponent
                {...(props as EmotionJSX.LibraryManagedAttributes<
                    typeof WrappedComponent,
                    P
                >)}
            />
        </ErrorBoundary>
    );
}
